import { Component, OnInit, AfterViewInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { SharedModule, FormInputComponent, StringCustomFieldConfig, NotificationService, Asset, DataService, DynamicFormInputComponent, ModalService } from '@vendure/admin-ui/core';
import * as fabric from 'fabric';
import { safeJSONParse } from '@common-ui/utils';
import {
  BackgroundImageDisplayType,
  CustomizedPicturePatternData,
  ImageFrameData,
  LabeledImageData,
  TextFrameGroupConfig,
  DATA_LOADING_TIME_IN_MS,
  DEFAULT_LABEL_FONT_SIZE,
  DEFAULT_LABEL_COLOR,
} from '../../common/ui-types';
import {
  ID,
} from '@vendure/common/lib/shared-types';
import { forkJoin, Observable } from 'rxjs';
import { notNullOrUndefined } from '@vendure/common/lib/shared-utils';
import { TextFramesFormComponent } from '../text-frames-form.component';

@Component({
  selector: 'pattern-editor-input-form',
  templateUrl: './pattern-editor-input-form.component.html',
  styleUrls: [
    './pattern-editor-input-form.component.scss',
    '../../../common/ui/styles.scss'
  ],
  standalone: true,
  imports: [SharedModule, TextFramesFormComponent]
})
export class PatternEditorInputFormComponent implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit {
  @ViewChild('backgroundImagesInput') backgroundImagesInput: DynamicFormInputComponent;
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: UntypedFormControl;

  customizedPicturePattern: CustomizedPicturePatternData;
  // Form controls
  labelControl = new UntypedFormControl();
  descriptionControl = new UntypedFormControl();
  minWidthControl = new UntypedFormControl();
  minHeightControl = new UntypedFormControl();
  canvasWidthControl = new UntypedFormControl();
  canvasHeightControl = new UntypedFormControl();
  backgroundImageDisplayTypeControl = new UntypedFormControl();
  backgroundImagesControl = new UntypedFormControl();
  selectedFrameNameControl = new UntypedFormControl();
  selectedFrameLabelControl = new UntypedFormControl();
  selectedFrameLabelColorControl = new UntypedFormControl(DEFAULT_LABEL_COLOR);
  selectedFrameLabelFontSizeControl = new UntypedFormControl(DEFAULT_LABEL_FONT_SIZE);
  selectedFrameXControl = new UntypedFormControl();
  selectedFrameYControl = new UntypedFormControl();
  selectedFrameWidthControl = new UntypedFormControl();
  selectedFrameHeightControl = new UntypedFormControl();

  // Canvas elements
  private canvas: fabric.Canvas;
  updatedByCanvas = false;

  // Canvas dimensions
  private canvasWidth = 800;
  private canvasHeight = 800;
  maxCanvasWidth = 800;
  maxCanvasHeight = 800;
  squareSize = 240;
  maxLabelWindowRatio = 0.8;
  maxLabelTextLength = 100;
  rulerMarks: number[] = [0, 100, 200, 300, 400, 500, 600, 700, 800];
  transparency = 0.5;
  // Pattern properties
  patternGroups: fabric.Group[] = [];
  rectCount = 1;
  activeRectIndex: number = -1;
  // Background properties
  backgroundImage: fabric.Image | null = null;
  initialLoaded = false;
  private backgroundImageUrl: string | null = null;

  constructor(
    private notificationService: NotificationService,
    private dataService: DataService,
    private changeDetector: ChangeDetectorRef,
    private modalService: ModalService,
  ) { }

  /**
   * Per-frame text-overlay groups for the currently selected image-frame. Bound
   * into the embedded `<text-frames-form>` sub-form below the X/Y/W/H controls.
   * Defaults to `[]` when no frame is selected (the sub-form is hidden then).
   */
  selectedFrameTextGroups: TextFrameGroupConfig[] = [];

  /**
   * Frame name used to seed new nested text-overlay group names
   * ("{frame} Text Overlay"). Falls back to "Photo {n}" for unnamed frames —
   * the same convention the storefront customizer uses.
   */
  get selectedFrameDefaultName(): string {
    const name = (this.selectedFrameNameControl.value as string | null)?.trim();
    return name || (this.activeRectIndex >= 0 ? `Photo ${this.activeRectIndex + 1}` : 'Photo');
  }

  // Shared options for frame Groups. Fabric 7 defaults originX/originY to 'center',
  // but our stored positions use top-left coordinates — keep left/top origin.
  private readonly frameGroupDefaults = {
    originX: 'left' as const,
    originY: 'top' as const,
    cornerSize: 8,
    transparentCorners: false,
    cornerColor: 'blue',
    cornerStyle: 'circle' as const,
    hasControls: true,
    hasBorders: true,
    lockRotation: true,
  };

  ngOnInit(): void {
    // Try to load existing configuration
    this.loadExistingConfig();
    this.initializeFormControls();
  }

  ngAfterViewInit(): void {
    // Set up subscriptions for the extra form controls so that any change triggers an update.
    this.labelControl.valueChanges.subscribe(this.onLabelChange);
    this.descriptionControl.valueChanges.subscribe(this.onDescriptionChange);
    this.minWidthControl.valueChanges.subscribe(this.onMinWidthChange);
    this.minHeightControl.valueChanges.subscribe(this.onMinHeightChange);

    this.backgroundImageDisplayTypeControl.valueChanges.subscribe(this.onBackgroundImageDisplayTypeChange);
    if(this.backgroundImagesInput) {
      this.backgroundImagesInput.registerOnChange(this.onBackgroundImagesChange);
      if(this.customizedPicturePattern?.backgroundImages) {
        const backgroundImageJsons = this.customizedPicturePattern?.backgroundImages?.map((value) => JSON.stringify(value)) || [];
          this.backgroundImagesInput.writeValue(backgroundImageJsons);
      }
    }
    this.canvasWidthControl.valueChanges.subscribe(this.onCanvasWidthChange);
    this.canvasHeightControl.valueChanges.subscribe(this.onCanvasHeightChange);
    // Subscriptions for selected frame properties: when edited, update the active object.
    this.selectedFrameNameControl.valueChanges.subscribe(this.onSelectedFrameNameChange);
    this.selectedFrameLabelControl.valueChanges.subscribe(this.onSelectedFrameLabelChange);
    this.selectedFrameLabelColorControl.valueChanges.subscribe(this.onSelectedFrameLabelColorChange);
    this.selectedFrameLabelFontSizeControl.valueChanges.subscribe(this.onSelectedFrameLabelFontSizeChange);
    this.selectedFrameXControl.valueChanges.subscribe(this.onSelectedFrameXChange);
    this.selectedFrameYControl.valueChanges.subscribe(this.onSelectedFrameYChange);
    this.selectedFrameWidthControl.valueChanges.subscribe(this.onSelectedFrameWidthChange);
    this.selectedFrameHeightControl.valueChanges.subscribe(this.onSelectedFrameHeightChange);
    // Wait for DOM to be ready
    setTimeout(() => {
      this.initializeCanvas();
    }, DATA_LOADING_TIME_IN_MS);
  }

  loadExistingConfig(): void {
    const defaultCustomizedPicturePatternData: CustomizedPicturePatternData = {
      label: '',
      description: '',
      minWidth: 0,
      minHeight: 0,
      canvasWidth: 800,
      canvasHeight: 800,
      backgroundImageDisplayType: 'name-list',
      backgroundImages: [],
      imageFrames: [{
          name: 'Photo',
          positionX: 200,
          positionY: 200,
          sizeX: 400,
          sizeY: 400,
      }],
    };
    this.customizedPicturePattern = safeJSONParse<CustomizedPicturePatternData>(
      this.formControl.value,
      this.notificationService,
      defaultCustomizedPicturePatternData,
    )!;
    this.canvasWidth = this.customizedPicturePattern?.canvasWidth || this.canvasWidth;
    this.canvasHeight = this.customizedPicturePattern?.canvasHeight || this.canvasHeight;
  }

  private initializeFormControls() {
    this.labelControl.setValue(this.customizedPicturePattern?.label);
    this.descriptionControl.setValue(this.customizedPicturePattern?.description);
    this.minWidthControl.setValue(this.customizedPicturePattern?.minWidth);
    this.minHeightControl.setValue(this.customizedPicturePattern?.minHeight);
    this.canvasWidthControl.setValue(this.customizedPicturePattern?.canvasWidth);
    this.canvasHeightControl.setValue(this.customizedPicturePattern?.canvasHeight);
    this.backgroundImageDisplayTypeControl.setValue(this.customizedPicturePattern?.backgroundImageDisplayType);
  }

  async initializeCanvas(): Promise<void> {
    this.canvas = new fabric.Canvas('patternCanvas', {
      width: this.canvasWidth,
      height: this.canvasHeight,
      backgroundColor: '#f0f0f0',
      selection: true
    });

    this.canvas.on('selection:created', (e: any) => {
      const selectedObject = e.selected[0] as fabric.Group;
      this.updateSelectedFrameProperties(selectedObject);
    });

    this.canvas.on('selection:updated', (e: any) => {
      const selectedObject = e.selected[0] as fabric.Group;
      this.updateSelectedFrameProperties(selectedObject);
    });

    this.canvas.on('selection:cleared', () => {
      this.cleanSelectedFrameProperties();
    });

    this.canvas.on('object:modified', (e: any) => {
      const selectedObject = e.target as fabric.Group;
      this.updateSelectedFrameProperties(selectedObject);
    });

    if (this.customizedPicturePattern) {
      await this.loadConfigToCanvas();
    } else {
      this.resetCanvas();
    }
    console.log('Canvas initialized with width:', this.canvasWidth, 'and height:', this.canvasHeight);
    this.initialLoaded = true;
  }

  private updateSelectedFrameProperties(selectedObject: fabric.Group): void {
    this.updatedByCanvas = true;
    this.rectCount = this.patternGroups.length;
    this.activeRectIndex = selectedObject?.get('customProperties')?.index;
    const box = selectedObject.getBoundingRect();

    // Use Math.round for positions and dimensions
    const positionX = Math.round(box.left);
    const positionY = Math.round(box.top);
    const width = Math.floor(box.width);
    const height = Math.floor(box.height);
    this.selectedFrameXControl.setValue(positionX, { emitEvent: false });
    this.selectedFrameYControl.setValue(positionY, { emitEvent: false });
    this.selectedFrameWidthControl.setValue(width, { emitEvent: false });
    this.selectedFrameHeightControl.setValue(height, { emitEvent: false });
    const label = selectedObject?.get('customProperties')?.label as fabric.Textbox;
    this.selectedFrameNameControl.setValue(selectedObject?.get('customProperties')?.name ?? '', { emitEvent: false });
    this.selectedFrameLabelControl.setValue(label?.text ?? '', { emitEvent: false });
    this.selectedFrameLabelColorControl.setValue(label?.fill ?? DEFAULT_LABEL_COLOR, { emitEvent: false });
    this.selectedFrameLabelFontSizeControl.setValue(label?.fontSize ?? DEFAULT_LABEL_FONT_SIZE, { emitEvent: false });
    // Populate per-frame text-overlay groups for the embedded sub-form.
    this.refreshSelectedFrameTextGroups();
    this.updatedByCanvas = false;
    this.changeDetector.markForCheck();
    this.updateFormValue();
  }

  private cleanSelectedFrameProperties(): void {
    this.activeRectIndex = -1;
    this.selectedFrameXControl.setValue(null, { emitEvent: false });
    this.selectedFrameYControl.setValue(null, { emitEvent: false });
    this.selectedFrameWidthControl.setValue(null, { emitEvent: false });
    this.selectedFrameHeightControl.setValue(null, { emitEvent: false });
    this.selectedFrameNameControl.setValue('', { emitEvent: false });
    this.selectedFrameLabelControl.setValue('', { emitEvent: false });
    this.selectedFrameLabelColorControl.setValue(DEFAULT_LABEL_COLOR, { emitEvent: false });
    this.selectedFrameLabelFontSizeControl.setValue(DEFAULT_LABEL_FONT_SIZE, { emitEvent: false });
    // Clear per-frame text-overlay sub-form state.
    this.selectedFrameTextGroups = [];
    this.changeDetector.markForCheck();
  }

  /**
   * Read the persisted nested text-overlay groups for the active image-frame
   * into `selectedFrameTextGroups` so the embedded `<text-frames-form>`
   * re-renders for the selected frame.
   */
  private refreshSelectedFrameTextGroups(): void {
    if (this.activeRectIndex < 0
        || !this.customizedPicturePattern
        || !this.customizedPicturePattern.imageFrames
        || this.activeRectIndex >= this.customizedPicturePattern.imageFrames.length) {
      this.selectedFrameTextGroups = [];
      return;
    }
    const frame = this.customizedPicturePattern.imageFrames?.[this.activeRectIndex];
    // Pass a fresh array reference so OnChanges fires on the embedded sub-form.
    // Guard malformed imported JSON (e.g. textFrameGroups: {}) — .slice() on a
    // non-array would crash before the sub-form's own normalization can run.
    this.selectedFrameTextGroups = Array.isArray(frame?.textFrameGroups)
      ? frame!.textFrameGroups.slice()
      : [];
  }

  /**
   * Write back to `imageFrames[activeRectIndex].textFrameGroups` when the
   * embedded `<text-frames-form>` emits `groupsChanged`. Persists via the
   * existing `updateFormValue()` dirty-tracking path.
   */
  onSelectedFrameTextGroupsChanged(groups: TextFrameGroupConfig[]): void {
    if (this.activeRectIndex < 0
        || !this.customizedPicturePattern
        || !this.customizedPicturePattern.imageFrames
        || this.activeRectIndex >= this.customizedPicturePattern.imageFrames.length) {
      return;
    }
    const frame = this.customizedPicturePattern.imageFrames[this.activeRectIndex];
    // Empty arrays are normalised to `undefined` to keep DB rows minimal and
    // preserve backward compatibility with legacy rows.
    if (!groups || groups.length === 0) {
      delete frame.textFrameGroups;
    } else {
      frame.textFrameGroups = groups;
    }
    this.selectedFrameTextGroups = groups ?? [];
    this.updateFormValue();
  }

  async loadConfigToCanvas(): Promise<void> {
    this.canvas.clear();
    this.patternGroups = [];
    await this.updateCanvasBackground();
    const imageFrames = this.customizedPicturePattern?.imageFrames || [];
    this.rectCount = imageFrames.length;

    if (Array.isArray(imageFrames) && imageFrames.length > 0) {
        imageFrames.forEach((frameConfig, index) => {
            const rectWidth = frameConfig.sizeX;
            const rectHeight = frameConfig.sizeY;

            // Define the rectangle with its size
            const rect = new fabric.Rect({
                width: rectWidth,
                height: rectHeight,
                fill: `rgba(255, 255, 255, ${this.transparency})`,
                originX: 'center',
                originY: 'center',
            });

            const labelText = frameConfig.label ?? `#${index + 1}`;
            const label = new fabric.Textbox(labelText, {
              fontSize: frameConfig.labelFontSize ?? DEFAULT_LABEL_FONT_SIZE,
              fill: frameConfig.labelColor ?? DEFAULT_LABEL_COLOR,
              fontWeight: 'bold',
              originX: 'center',
              originY: 'center',
            });

            const frameName = frameConfig.name ?? `Photo ${index + 1}`;

            // Create the group, positioning it absolutely
            const group = new fabric.Group([rect, label], {
                ...this.frameGroupDefaults,
                left: frameConfig.positionX,
                top: frameConfig.positionY,
            });

            // Label scaling logic (should still work)
            const initialLabelScaleX = 1 / group.scaleX;
            const initialLabelScaleY = 1 / group.scaleY;
            label.set({ scaleX: initialLabelScaleX, scaleY: initialLabelScaleY });
            group.set('customProperties', { index, rect, label, name: frameName });
            group.on('scaling', () => {
                const { scaleX, scaleY } = group;
                (group.get('customProperties').label as fabric.Textbox)?.set({
                     scaleX: 1 / scaleX, scaleY: 1 / scaleY
                 });
                 this.fitLabelToFrame(group);
            });
            this.fitLabelToFrame(group);
            this.patternGroups.push(group);
            this.canvas.add(group);
        });
    }
    this.canvas.requestRenderAll();
  }

  /**
   * "Reset Canvas" button handler. Mirrors removeFrame()'s cascade-confirm: if
   * any frame carries nested text-overlay groups, confirm before discarding
   * them — a reset wipes ALL frames at once, so it must not silently destroy
   * authored overlays. The init path calls resetCanvas() directly (no pattern /
   * no overlays exist yet there), so the confirm lives only on this button path.
   */
  resetCanvasWithConfirm(): void {
    const nestedGroupCount = (this.customizedPicturePattern?.imageFrames ?? [])
      .reduce((sum, f: any) => sum + (Array.isArray(f?.textFrameGroups) ? f.textFrameGroups.length : 0), 0);
    if (nestedGroupCount === 0) {
      this.resetCanvas();
      return;
    }
    this.modalService.dialog({
      title: 'Reset canvas and discard all text overlays?',
      body: `This pattern has ${nestedGroupCount} text-overlay group(s) across its frames. `
          + `Resetting clears every frame and all overlays. Continue?`,
      buttons: [
        { type: 'secondary', label: 'Cancel', returnValue: false },
        { type: 'danger',    label: 'Reset',  returnValue: true  },
      ],
    }).subscribe((confirmed: boolean | undefined) => {
      if (confirmed) this.resetCanvas();
    });
  }

  resetCanvas(): void {
    this.canvas.clear();
    this.patternGroups = [];
    // Discard the persisted frame model so generateImageFramesFromCanvas() does
    // not carry the old frames' nested textFrameGroups over onto the fresh frame
    // (index/name carry-over would otherwise re-attach overlays to the reset).
    if (this.customizedPicturePattern) {
      this.customizedPicturePattern.imageFrames = [];
    }
    this.selectedFrameTextGroups = [];
    if (this.backgroundImage) {
      this.canvas.add(this.backgroundImage);
    }
    this.rectCount = 1;
    this.addRect();
  }

  addRect(): void {
    const rectIndex = this.patternGroups.length;
    const rectWidth = this.squareSize / this.rectCount;
    const rectHeight = this.squareSize / this.rectCount;

    const rect = new fabric.Rect({
      width: rectWidth,
      height: rectHeight,
      fill: `rgba(255, 255, 255, ${this.transparency})`,
      originX: 'center',
      originY: 'center',
    });

    const defaultName = `Photo ${rectIndex + 1}`;
    const label = new fabric.Textbox(`#${rectIndex + 1}`, {
      fontSize: DEFAULT_LABEL_FONT_SIZE,
      fill: DEFAULT_LABEL_COLOR,
      fontWeight: 'bold',
      originX: 'center',
      originY: 'center',
    });

    const group = new fabric.Group([rect, label], {
      ...this.frameGroupDefaults,
      left: (this.canvasWidth - rectWidth) / 2 + (rectIndex * 20),
      top: (this.canvasHeight - rectHeight) / 2 + (rectIndex * 20),
    });

    group.set('customProperties', {
      index: rectIndex,
      rect: rect,
      label: label,
      name: defaultName
    });

    group.on('scaling', () => {
      // The group's new scale factors
      const { scaleX, scaleY } = group;
      group.get('customProperties').label.set({
        scaleX: 1 / scaleX,
        scaleY: 1 / scaleY
      });
      this.canvas.requestRenderAll();
    });

    // Add both to canvas
    this.patternGroups.push(group);
    this.canvas.add(group);

    this.canvas.requestRenderAll();
    this.updateFormValue();
  }

  removeRect(): void {
    if (this.patternGroups.length > 1) {
      // Delegate to removeFrame(lastIndex) so cascade-confirm is consistent
      // between the "Delete Last Frame" toolbar button and the selection-driven
      // delete affordance below.
      this.removeFrame(this.patternGroups.length - 1);
    }
  }

  /**
   * Delete the currently-selected frame (the one being edited), not the trailing
   * one. Delegates to removeFrame() so the nested-overlay cascade-confirm and
   * re-indexing are shared. No-op when nothing is selected or only one frame
   * remains (removeFrame guards both).
   */
  removeSelectedFrame(): void {
    if (this.activeRectIndex >= 0) {
      this.removeFrame(this.activeRectIndex);
    }
  }

  /**
   * Frame delete with cascade-confirm for nested text-overlay groups.
   *
   * Frames with zero nested groups delete silently. Frames with ≥1 nested group
   * surface a modal — OK cascades the delete, Cancel is a no-op. After delete we
   * also clear the per-frame sub-form state if the deleted frame was selected.
   */
  removeFrame(index: number): void {
    if (index < 0 || index >= this.patternGroups.length) return;
    if (this.patternGroups.length <= 1) return;

    const frameData = this.customizedPicturePattern?.imageFrames?.[index];
    const nestedGroupCount = frameData?.textFrameGroups?.length ?? 0;
    const frameName = frameData?.name ?? `Photo ${index + 1}`;

    const doDelete = () => this.performFrameDelete(index);

    if (nestedGroupCount === 0) {
      doDelete();
      return;
    }

    this.modalService.dialog({
      title: 'Delete frame and its text overlays?',
      body: `Frame "${frameName}" has ${nestedGroupCount} text-overlay group(s). `
          + `Delete frame and all overlays?`,
      buttons: [
        { type: 'secondary', label: 'Cancel', returnValue: false },
        { type: 'danger',    label: 'Delete', returnValue: true  },
      ],
    }).subscribe((confirmed: boolean | undefined) => {
      if (confirmed) doDelete();
    });
  }

  private performFrameDelete(index: number): void {
    const wasActive = this.activeRectIndex === index;
    const [removedGroup] = this.patternGroups.splice(index, 1);
    if (removedGroup && this.canvas) {
      this.canvas.remove(removedGroup);
    }
    if (this.customizedPicturePattern?.imageFrames) {
      this.customizedPicturePattern.imageFrames.splice(index, 1);
    }
    // Re-stamp customProperties.index so subsequent selection events resolve
    // to the right slot in `imageFrames[]`.
    this.patternGroups.forEach((g, i) => {
      const cp = g.get('customProperties') ?? {};
      g.set('customProperties', { ...cp, index: i });
    });
    if (wasActive) {
      this.cleanSelectedFrameProperties();
    } else if (this.activeRectIndex > index) {
      // Shift the active index left to track the surviving frame.
      this.activeRectIndex = this.activeRectIndex - 1;
    }
    if (this.canvas) this.canvas.requestRenderAll();
    this.updateFormValue();
  }

  private _applyBackgroundScaling(img: fabric.Image): void {
    if (!this.canvas || !img || !img.width || !img.height) {
        // Ensure background is cleared if image is invalid
        if(this.canvas && this.backgroundImage) {
            this.canvas.remove(this.backgroundImage);
            this.canvas.backgroundImage = undefined;
        }
        return;
    }
    const canvasWidth = this.canvas.getWidth();
    const canvasHeight = this.canvas.getHeight();
    const originalSize = img.getOriginalSize();

    if (originalSize.width === 0 || originalSize.height === 0) {
        if(this.canvas && this.backgroundImage) {
          this.canvas.remove(this.backgroundImage);
          this.canvas.backgroundImage = undefined;
        }
        return;
    }

    const scaleXFactor = canvasWidth / originalSize.width;
    const scaleYFactor = canvasHeight / originalSize.height;

    // Use the LARGER scale factor to ensure coverage ("cover" behavior)
    const scale = Math.max(scaleXFactor, scaleYFactor);

    img.set({
        scaleX: scale,
        scaleY: scale,
        // Center the image on the canvas
        originX: 'center',
        originY: 'center',
        left: canvasWidth / 2,
        top: canvasHeight / 2
    });

    console.log(`Background scaled to: ${scale.toFixed(3)}`);
  }

  updateBackgroundImage(url: string): void {
    if (this.backgroundImage) {
      this.canvas.remove(this.backgroundImage);
    }
    // If the URL is the same, just re-apply scaling (e.g., after canvas resize)
    if (this.backgroundImage && this.backgroundImageUrl === url) {
        this._applyBackgroundScaling(this.backgroundImage);
        this.canvas.backgroundImage = this.backgroundImage;
        this.canvas.requestRenderAll();
        return;
    }
     this.backgroundImageUrl = url;

     fabric.FabricImage.fromURL(url, { crossOrigin: 'anonymous' }).then((img) => {
         this.backgroundImage = img;
         this.backgroundImage.set({ selectable: false });
         this._applyBackgroundScaling(this.backgroundImage);
         this.canvas.backgroundImage = this.backgroundImage;
         this.canvas.requestRenderAll();
     }).catch(err => {
         console.error("Error loading background image:", err);
         this.notificationService.error('Failed to load background image.');
         this.backgroundImage = null;
         this.backgroundImageUrl = null;
         this.canvas.requestRenderAll();
     });
  }

  private generateImageFramesFromCanvas(): ImageFrameData[] {
    if (!this.canvas || !this.patternGroups) {
        return [];
    }
    // Preserve any existing nested `textFrameGroups` per frame, since they live
    // in sidecar JSONB and are NOT projected onto the Fabric canvas. We match by
    // canvas index first, falling back to frame name to remain robust if the
    // array order has drifted (e.g. selection-driven reorders).
    const previousFrames = this.customizedPicturePattern?.imageFrames ?? [];
    const previousByName: Record<string, ImageFrameData> = {};
    for (const pf of previousFrames) {
        if (pf?.name) previousByName[pf.name] = pf;
    }
    return this.patternGroups.map((group, idx) => {
        const box = group.getBoundingRect();
        const labelObj = group.get('customProperties').label as fabric.Textbox;
        const name = group.get('customProperties').name ?? `Photo ${group.get('customProperties').index + 1}`;
        const carryOver = previousFrames[idx] ?? previousByName[name];
        const next: ImageFrameData = {
            name,
            label: labelObj.text ?? '',
            labelColor: (labelObj.fill as string) ?? DEFAULT_LABEL_COLOR,
            labelFontSize: labelObj.fontSize ?? DEFAULT_LABEL_FONT_SIZE,
            positionX: Math.round(box.left),
            positionY: Math.round(box.top),
            sizeX: Math.floor(box.width),
            sizeY: Math.floor(box.height),
        };
        if (carryOver?.textFrameGroups && carryOver.textFrameGroups.length > 0) {
            next.textFrameGroups = carryOver.textFrameGroups;
        }
        return next;
    });
  }

  private loadAssetsByIds(ids: ID[]): Observable<Asset[]> {
    return forkJoin(
      ids?.map((id) =>
        this.dataService.product
          .getAsset(id.toString())
          .mapSingle((res) => res.asset)
      )
    ) as Observable<Asset[]>;
  }

  private syncCanvasStateToDataModel(): void {
    if(!this.initialLoaded) return;

    if (!this.customizedPicturePattern) {
         console.error("Cannot sync state, customizedPicturePattern is null.");
         return;
    }
    this.customizedPicturePattern.imageFrames = this.generateImageFramesFromCanvas();
    this.customizedPicturePattern.label = this.labelControl.value;
    this.customizedPicturePattern.description = this.descriptionControl.value;
    this.customizedPicturePattern.minWidth = this.minWidthControl.value;
    this.customizedPicturePattern.minHeight = this.minHeightControl.value;
    this.customizedPicturePattern.canvasWidth = this.canvasWidthControl.value;
    this.customizedPicturePattern.canvasHeight = this.canvasHeightControl.value;
  }

  private onLabelChange = (value: string) => {
    if (this.customizedPicturePattern?.label !== value) {
      this.customizedPicturePattern!.label = value;
      this.updateFormValue();
    }
  }

  private onDescriptionChange = (value: string) => {
    if (this.customizedPicturePattern?.description !== value) {
      this.customizedPicturePattern!.description = value;
      this.updateFormValue();
    }
  };

  private onMinWidthChange = (value: number) => {
    if (this.customizedPicturePattern?.minWidth !== value) {
      this.customizedPicturePattern!.minWidth = value;
      this.updateFormValue();
    }
  };

  private onMinHeightChange = (value: number) => {
    if (this.customizedPicturePattern?.minHeight !== value) {
      this.customizedPicturePattern!.minHeight = value;
      this.updateFormValue();
    }
  };

  private onBackgroundImageDisplayTypeChange = (value: BackgroundImageDisplayType) => {
    if (this.customizedPicturePattern?.backgroundImageDisplayType !== value) {
      this.customizedPicturePattern!.backgroundImageDisplayType = value;
      this.updateFormValue();
    }
  };

  private onBackgroundImagesChange = async (values: string[]) => {
    if (this.customizedPicturePattern?.backgroundImages && JSON.stringify(this.customizedPicturePattern.backgroundImages) !== JSON.stringify(values)) {
      const defaultLabeledImageData: LabeledImageData = {
        label: '',
        assetId: null,
      };
      this.customizedPicturePattern.backgroundImages = values?.map((value) => safeJSONParse<LabeledImageData>(
        value,
        this.notificationService,
        defaultLabeledImageData
      )).filter(notNullOrUndefined) ?? [];
      this.updateFormValue();
      const backgroundImageId = this.customizedPicturePattern?.backgroundImages[0]?.assetId;
      if (backgroundImageId) {
        const assets = await this.loadAssetsByIds([backgroundImageId]).toPromise();
        if(assets && assets[0]?.preview) {
          this.updateBackgroundImage(assets[0].preview);
        }
      } else {
        if(this.backgroundImage) {
          this.canvas.remove(this.backgroundImage);
          this.canvas.backgroundImage = undefined;
          this.backgroundImage = null;
          this.canvas.requestRenderAll();
        }
      }
    }
  };

  private onCanvasWidthChange = async (value: number) => {
      if (this.customizedPicturePattern && this.customizedPicturePattern.canvasWidth !== value) {
          this.canvasWidth = value;
          this.customizedPicturePattern.canvasWidth = value;
          if (this.canvas) {
              this.canvas.setDimensions({ width: value });
              await this.updateCanvasBackground();
              this.canvas.requestRenderAll();
          }
      }
  }

  private onCanvasHeightChange = async (value: number) => {
      if (this.customizedPicturePattern && this.customizedPicturePattern.canvasHeight !== value) {
          this.canvasHeight = value;
          this.customizedPicturePattern.canvasHeight = value;
          if (this.canvas) {
              this.canvas.setDimensions({ height: value });
              await this.updateCanvasBackground();
              this.canvas.requestRenderAll();
          }
      }
  }

  private async updateCanvasBackground(): Promise<void> {
    if (!this.canvas) return;
    if (this.backgroundImage instanceof fabric.Image && this.canvas) {
      this._applyBackgroundScaling(this.backgroundImage);
      this.canvas.backgroundImage = this.backgroundImage;
      return;
    }
    const backgroundImageId = this.customizedPicturePattern?.backgroundImages[0]?.assetId;
    if(backgroundImageId) {
      const assets = await this.loadAssetsByIds([backgroundImageId]).toPromise();
      if (assets && assets[0]?.preview) {
        this.updateBackgroundImage(assets[0].preview);
      }
    } else{
      console.log('No background image available to set.');
    }
  }

  private onSelectedFrameXChange = (value: number) => {
    if(this.updatedByCanvas) return;
    this.updateActiveRectProperties()
  };

  private onSelectedFrameYChange = (value: number) => {
    if(this.updatedByCanvas) return;
    this.updateActiveRectProperties()
  };

  private onSelectedFrameWidthChange = (value: number) => {
    if(this.updatedByCanvas) return;
    this.updateActiveRectProperties()
  };

  private onSelectedFrameHeightChange = (value: number) => {
    if(this.updatedByCanvas) return;
    this.updateActiveRectProperties()
  };

  private onSelectedFrameNameChange = (value: string) => {
    if (this.activeRectIndex < 0 ||
        this.activeRectIndex >= this.patternGroups.length) { return; }

    const group = this.patternGroups[this.activeRectIndex];
    const cp = group.get('customProperties');
    if (cp.name === value) { return; }

    cp.name = (value ?? '').slice(0, this.maxLabelTextLength/2);
    (group as any).dirty = true;
    this.canvas.requestRenderAll();
    this.updateFormValue();
  };

  private onSelectedFrameLabelChange = (value: string) => {
    if (this.activeRectIndex < 0 ||
        this.activeRectIndex >= this.patternGroups.length) { return; }

    const group = this.patternGroups[this.activeRectIndex];
    const label = group.get('customProperties').label as fabric.Textbox;
    const newText = (value ?? '').slice(0, this.maxLabelTextLength);
    if (label.text === newText) { return; }

    label.set('text', newText);
    label.initDimensions();
    label.setCoords();
    (group as any).dirty = true;
    group.setCoords();
    if (group.width) {
      label.set({ width: group.width, textAlign: 'center' });
    }
    this.fitLabelToFrame(group);
    this.canvas.requestRenderAll();
    this.updateFormValue();
  };

  private onSelectedFrameLabelColorChange = (value: string) => {
    if (this.activeRectIndex < 0 || this.activeRectIndex >= this.patternGroups.length) return;
    const group = this.patternGroups[this.activeRectIndex];
    const label = group.get('customProperties').label as fabric.Textbox;
    label.set('fill', value || DEFAULT_LABEL_COLOR);
    (group as any).dirty = true;
    this.canvas.requestRenderAll();
    this.updateFormValue();
  };

  private onSelectedFrameLabelFontSizeChange = (value: number) => {
    if (this.activeRectIndex < 0 || this.activeRectIndex >= this.patternGroups.length) return;
    const size = Math.max(8, Math.min(200, +value || DEFAULT_LABEL_FONT_SIZE));
    const group = this.patternGroups[this.activeRectIndex];
    const label = group.get('customProperties').label as fabric.Textbox;
    label.set('fontSize', size);
    label.initDimensions();
    label.setCoords();
    (group as any).dirty = true;
    this.fitLabelToFrame(group);
    this.canvas.requestRenderAll();
    this.updateFormValue();
  };

  private fitLabelToFrame(group: fabric.Group): void {
    const { rect, label } = group.get('customProperties') as {
      rect: fabric.Rect;
      label: fabric.Textbox;
    };
    const frameW = rect.width! * group.scaleX;
    const targetW = frameW * this.maxLabelWindowRatio;
    label.set({
      width: targetW,
      textAlign: 'center',
      originX: 'center',
      left: 0,
    });
    label.initDimensions();
    label.setCoords();
    (group as any).dirty = true;
  }

  updateActiveRectProperties(): void {
    if (this.activeRectIndex < 0 || this.activeRectIndex >= this.patternGroups.length) {
        console.warn('updateActiveRectProperties called with invalid index:', this.activeRectIndex);
      return;
    }
    const group = this.patternGroups[this.activeRectIndex];
    if (!group) {
        console.warn('updateActiveRectProperties could not find group at index:', this.activeRectIndex);
      return;
    }
    // Get desired values from form controls
    const newLeft = +this.selectedFrameXControl.value;
    const newTop = +this.selectedFrameYControl.value;
    const newWidth = +this.selectedFrameWidthControl.value;
    const newHeight = +this.selectedFrameHeightControl.value;

    const originalRectWidth = (group.get('customProperties').rect as fabric.Rect)?.width;
    const originalRectHeight = (group.get('customProperties').rect as fabric.Rect)?.height;
    if (originalRectWidth && originalRectHeight && originalRectWidth > 0 && originalRectHeight > 0) {
        const scaleX = newWidth / originalRectWidth;
        const scaleY = newHeight / originalRectHeight;
        group.set({
            left: newLeft,
            top: newTop,
            scaleX: scaleX,
            scaleY: scaleY,
        });
    } else {
        console.warn('Cannot calculate scale, original dimensions invalid:', originalRectWidth, originalRectHeight);
        group.set({ left: newLeft, top: newTop });
    }
    const label = group.get('customProperties').label as fabric.Textbox;
    if (label) {
        label.set({
            left: 0,
            top: 0,
            scaleX: 1 / group.scaleX,
            scaleY: 1 / group.scaleY
        });
    }
    group.setCoords();
    this.fitLabelToFrame(group);
    this.canvas.requestRenderAll();
    this.updateFormValue();
  }

  updateFormValue(): void {
    if (this.formControl) {
      this.syncCanvasStateToDataModel();
      this.formControl.setValue(this.customizedPicturePattern);
      if(this.initialLoaded) {
        this.formControl.markAsDirty();
      }
    }
  }

}
